/**
 * Companies Page - Display and manage companies
 */

import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { 
  Search, 
  Building, 
  Users, 
  FileBarChart, 
  RefreshCw,
  Plus,
  Mail,
  Phone,
  MapPin,
  MoreHorizontal,
  Edit,
  Trash2
} from 'lucide-react';
import { 
  useSupabaseCompanies, 
  useCompanyStats, 
  useCompaniesCache,
  SupabaseCompany 
} from '@/hooks/useSupabaseCompanies';
import { useDebounce } from '@/hooks/useDebounce';
import { CompanyDialog } from '@/components/CompanyDialog';
import { CompanyDeleteDialog } from '@/components/CompanyDeleteDialog';

export default function Companies() {
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 300);
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<SupabaseCompany | null>(null);
  
  const { invalidateAll } = useCompaniesCache();

  // Fetch companies with search filter
  const {
    data: companies = [],
    isLoading: companiesLoading,
    error: companiesError,
    refetch: refetchCompanies
  } = useSupabaseCompanies({
    search: debouncedSearch || undefined,
    is_active: true
  });

  // Fetch company statistics
  const {
    data: stats,
    isLoading: statsLoading,
    error: statsError
  } = useCompanyStats();

  const handleRefresh = () => {
    invalidateAll();
    refetchCompanies();
  };

  // Dialog handlers
  const handleCreateCompany = () => {
    setSelectedCompany(null);
    setCreateDialogOpen(true);
  };

  const handleEditCompany = (company: SupabaseCompany) => {
    setSelectedCompany(company);
    setEditDialogOpen(true);
  };

  const handleDeleteCompany = (company: SupabaseCompany) => {
    setSelectedCompany(company);
    setDeleteDialogOpen(true);
  };

  if (companiesLoading && companies.length === 0) {
    return (
      <div className="p-6 max-w-7xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-10 w-32" />
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-8 w-16" />
              </CardHeader>
            </Card>
          ))}
        </div>
        
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-40" />
          </CardHeader>
          <CardContent>
            <Skeleton className="h-64 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (companiesError) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <Alert variant="destructive">
          <AlertDescription className="flex items-center justify-between">
            <span>Lỗi khi tải companies: {companiesError.message}</span>
            <Button onClick={handleRefresh} variant="outline" size="sm">
              <RefreshCw className="h-4 w-4 mr-2" />
              Thử lại
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Companies</h1>
          <p className="text-muted-foreground">Quản lý thông tin công ty</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={handleRefresh} variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Làm mới
          </Button>
          <Button onClick={handleCreateCompany}>
            <Plus className="h-4 w-4 mr-2" />
            Thêm công ty
          </Button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Tổng số công ty</CardTitle>
            <Building className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {statsLoading ? <Skeleton className="h-7 w-8" /> : stats?.totalCompanies || 0}
            </div>
            <p className="text-xs text-muted-foreground">
              {stats?.activeCompanies || 0} hoạt động
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Có nhân viên</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {statsLoading ? <Skeleton className="h-7 w-8" /> : stats?.companiesWithUsers || 0}
            </div>
            <p className="text-xs text-muted-foreground">
              công ty có users
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Có teams</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {statsLoading ? <Skeleton className="h-7 w-8" /> : stats?.companiesWithTeams || 0}
            </div>
            <p className="text-xs text-muted-foreground">
              công ty có teams
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Có báo cáo</CardTitle>
            <FileBarChart className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {statsLoading ? <Skeleton className="h-7 w-8" /> : stats?.companiesWithReportGroups || 0}
            </div>
            <p className="text-xs text-muted-foreground">
              công ty có report groups
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Companies Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Danh sách công ty</CardTitle>
              <CardDescription>
                {companies.length} công ty {searchTerm && `phù hợp với "${searchTerm}"`}
              </CardDescription>
            </div>
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder="Tìm kiếm công ty..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {companies.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tên công ty</TableHead>
                  <TableHead>Mô tả</TableHead>
                  <TableHead>Liên hệ</TableHead>
                  <TableHead>Trạng thái</TableHead>
                  <TableHead>Ngày tạo</TableHead>
                  <TableHead className="w-[70px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {companies.map((company: SupabaseCompany) => (
                  <TableRow key={company.id}>
                    <TableCell>
                      <div className="font-medium">{company.company_name}</div>
                      <div className="text-sm text-muted-foreground">
                        ID: {company.legacy_id}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="max-w-xs">
                        {company.description ? (
                          <p className="text-sm truncate">{company.description}</p>
                        ) : (
                          <span className="text-muted-foreground text-sm">Chưa có mô tả</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        {company.email && (
                          <div className="flex items-center text-sm">
                            <Mail className="h-3 w-3 mr-1 text-muted-foreground" />
                            {company.email}
                          </div>
                        )}
                        {company.phone && (
                          <div className="flex items-center text-sm">
                            <Phone className="h-3 w-3 mr-1 text-muted-foreground" />
                            {company.phone}
                          </div>
                        )}
                        {company.address && (
                          <div className="flex items-center text-sm">
                            <MapPin className="h-3 w-3 mr-1 text-muted-foreground" />
                            <span className="truncate max-w-32">{company.address}</span>
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={company.is_active ? "default" : "secondary"}>
                        {company.is_active ? "Hoạt động" : "Tạm dừng"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        {new Date(company.created_at).toLocaleDateString('vi-VN')}
                      </div>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" className="h-8 w-8 p-0">
                            <span className="sr-only">Open menu</span>
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleEditCompany(company)}>
                            <Edit className="mr-2 h-4 w-4" />
                            Sửa
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => handleDeleteCompany(company)}
                            className="text-destructive"
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Xóa
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Building className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>
                {searchTerm ? 'Không tìm thấy công ty nào' : 'Chưa có công ty nào'}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialogs */}
      <CompanyDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        mode="create"
      />
      
      <CompanyDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        company={selectedCompany || undefined}
        mode="edit"
      />

      {selectedCompany && (
        <CompanyDeleteDialog
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
          company={selectedCompany}
        />
      )}
    </div>
  );
}